import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";
import { useEffect, useRef, useState } from "react";
import {
  ActionIcon, Alert, Badge, Button, Group, Loader, Modal, Stack, Switch, Table, Text, TextInput, Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Technician } from "../../api/types";

type Draft = { id: number | null; full_name: string; phone: string; notes: string };

/** Tehnicieni: listă (inclusiv inactivi) + adăugare / editare / (de)activare. */
export function TechniciansTab({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<number | null>(null);
  const seq = useRef(0);

  async function load() {
    const s = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const r = await api.listTechnicians(true);
      if (s === seq.current) setRows(r.technicians);
    } catch {
      if (s === seq.current) setError(true);
    } finally {
      if (s === seq.current) setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function errMsg(e: unknown) {
    const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
    if (code === "name_required") return t("freeIssues.tech.nameRequired");
    return code ? t(`freeIssues.errors.${code}`, { defaultValue: code }) : String(e);
  }

  async function save() {
    if (!draft || !draft.full_name.trim()) return;
    setSaving(true);
    const body = { full_name: draft.full_name.trim(), phone: draft.phone.trim() || null, notes: draft.notes.trim() || null };
    try {
      if (draft.id == null) await api.createTechnician(body);
      else await api.updateTechnician(draft.id, body);
      notifications.show({ color: "green", message: t("common.saved") });
      setDraft(null);
      void load();
    } catch (e) {
      notifications.show({ color: "red", title: t("common.saveFailed"), message: errMsg(e) });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(tech: Technician, active: boolean) {
    setToggling(tech.id);
    try {
      const r = await api.updateTechnician(tech.id, { active });
      setRows((rs) => rs.map((x) => (x.id === tech.id ? r.technician : x)));
    } catch (e) {
      notifications.show({ color: "red", title: t("common.saveFailed"), message: errMsg(e) });
    } finally {
      setToggling(null);
    }
  }

  const activeCell = (tech: Technician) =>
    canEdit ? (
      <Switch
        checked={tech.active}
        disabled={toggling === tech.id}
        onChange={(e) => void toggleActive(tech, e.currentTarget.checked)}
        label={tech.active ? t("common.active") : t("common.inactive")}
      />
    ) : (
      <Badge size="sm" variant="light" color={tech.active ? "green" : "gray"}>
        {tech.active ? t("common.active") : t("common.inactive")}
      </Badge>
    );

  const editBtn = (tech: Technician) =>
    canEdit && (
      <ActionIcon
        variant="subtle"
        aria-label={t("common.edit")}
        onClick={() => setDraft({ id: tech.id, full_name: tech.full_name, phone: tech.phone ?? "", notes: tech.notes ?? "" })}
      >
        <IconEdit size={16} />
      </ActionIcon>
    );

  if (loading && rows.length === 0) return <Group justify="center" p="md"><Loader size="sm" /></Group>;
  if (error) return <Alert color="red">{t("freeIssues.loadFailed")}</Alert>;

  return (
    <>
      {canEdit && (
        <Group justify="flex-end" mb="sm">
          <Button
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => setDraft({ id: null, full_name: "", phone: "", notes: "" })}
          >
            {t("freeIssues.tech.add")}
          </Button>
        </Group>
      )}
      {rows.length === 0 ? (
        <Text c="dimmed">{t("freeIssues.tech.empty")}</Text>
      ) : (
        <>
          <ResponsiveTable
            mobile={{
              rows,
              rowKey: (tech) => tech.id,
              title: (tech) => <Text span size="sm" fw={600} c={tech.active ? undefined : "dimmed"}>{tech.full_name}</Text>,
              subtitle: (tech) => tech.phone,
              badge: (tech) => (
                <Badge size="sm" variant="light" color={tech.active ? "green" : "gray"}>
                  {tech.active ? t("common.active") : t("common.inactive")}
                </Badge>
              ),
              fields: [
                ...(canEdit ? [{ label: t("freeIssues.tech.status"), render: (tech: Technician) => activeCell(tech) }] : []),
                { label: t("freeIssues.tech.notes"), render: (tech) => tech.notes ?? "", hidden: (tech) => !tech.notes },
              ],
              actions: canEdit ? (tech) => editBtn(tech) : undefined,
            }}
          >
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("freeIssues.tech.name")}</Table.Th>
                  <Table.Th>{t("freeIssues.tech.phone")}</Table.Th>
                  <Table.Th>{t("freeIssues.tech.notes")}</Table.Th>
                  <Table.Th>{t("freeIssues.tech.status")}</Table.Th>
                  {canEdit && <Table.Th />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((tech) => (
                  <Table.Tr key={tech.id} c={tech.active ? undefined : "dimmed"}>
                    <Table.Td>{tech.full_name}</Table.Td>
                    <Table.Td>{tech.phone ?? "—"}</Table.Td>
                    <Table.Td><Text size="sm" lineClamp={2}>{tech.notes ?? ""}</Text></Table.Td>
                    <Table.Td>{activeCell(tech)}</Table.Td>
                    {canEdit && <Table.Td>{editBtn(tech)}</Table.Td>}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ResponsiveTable>
        </>
      )}

      <Modal
        opened={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id == null ? t("freeIssues.tech.add") : t("freeIssues.tech.edit")}
      >
        {draft && (
          <Stack>
            <TextInput
              label={t("freeIssues.tech.name")}
              required
              value={draft.full_name}
              onChange={(e) => { const v = e.currentTarget.value; setDraft((d) => (d ? { ...d, full_name: v } : d)); }}
              data-autofocus
            />
            <TextInput
              label={t("freeIssues.tech.phone")}
              value={draft.phone}
              onChange={(e) => { const v = e.currentTarget.value; setDraft((d) => (d ? { ...d, phone: v } : d)); }}
              inputMode="tel"
            />
            <Textarea
              label={t("freeIssues.tech.notes")}
              value={draft.notes}
              onChange={(e) => { const v = e.currentTarget.value; setDraft((d) => (d ? { ...d, notes: v } : d)); }}
              autosize
              minRows={2}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDraft(null)}>{t("common.cancel")}</Button>
              <Button onClick={() => void save()} loading={saving} disabled={!draft.full_name.trim()}>
                {t("common.save")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
